import { useEffect, useRef, useState } from "react";
import { Plus, Trash2, Upload } from "lucide-react";
import { toast } from "sonner";
import { api, API_BASE, authedFetch } from "../../lib/api";

const AdminSubscribers = () => {
  const [subs, setSubs] = useState([]);
  const [form, setForm] = useState({ name: "", email: "", phone: "", notes: "" });
  const fileRef = useRef(null);

  const fetchAll = () => api.get("/admin/subscribers").then((r) => setSubs(r.data));

  useEffect(() => { fetchAll(); }, []);

  const add = async (e) => {
    e.preventDefault();
    try {
      await api.post("/admin/subscribers", form);
      toast.success("Subscriber added");
      setForm({ name: "", email: "", phone: "", notes: "" });
      fetchAll();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not add");
    }
  };

  const remove = async (id) => {
    if (!confirm("Delete this subscriber?")) return;
    await api.delete(`/admin/subscribers/${id}`);
    toast.success("Deleted");
    fetchAll();
  };

  const importCsv = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    try {
      const token = localStorage.getItem("swk_admin_token");
      const res = await authedFetch(`${API_BASE}/admin/subscribers/import`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
        body: fd,
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || "Import failed");
      toast.success(`Imported ${data.added} · Skipped ${data.skipped}`);
      fetchAll();
    } catch (err) {
      toast.error(err.message);
    } finally {
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  return (
    <div data-testid="admin-subscribers-page">
      <div className="border-b hairline pb-6 mb-8 flex flex-col sm:flex-row sm:justify-between sm:items-end gap-4">
        <div>
          <div className="overline mb-2">Newsletter</div>
          <h1 className="font-display text-4xl uppercase tracking-tighter">Subscribers</h1>
        </div>
        <div>
          <input ref={fileRef} type="file" accept=".csv,text/csv" onChange={importCsv} className="hidden" data-testid="admin-subscribers-csv-input" />
          <button onClick={() => fileRef.current?.click()} className="btn-secondary" data-testid="admin-subscribers-csv-btn">
            <Upload size={14} strokeWidth={2} /> Import CSV
          </button>
        </div>
      </div>

      <form onSubmit={add} className="grid sm:grid-cols-5 gap-4 mb-10 border hairline p-5" style={{ background: "#fff" }} data-testid="admin-subscriber-form">
        <input required placeholder="Name" className="input-box" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="admin-subscriber-name" />
        <input required type="email" placeholder="Email" className="input-box" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="admin-subscriber-email" />
        <input placeholder="Phone" className="input-box" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} data-testid="admin-subscriber-phone" />
        <input placeholder="Notes" className="input-box" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} data-testid="admin-subscriber-notes" />
        <button type="submit" className="btn-primary" data-testid="admin-subscriber-add"><Plus size={14} strokeWidth={2} /> Add</button>
      </form>

      <div className="border hairline overflow-x-auto" style={{ background: "#fff" }}>
        <table className="admin-table">
          <thead>
            <tr><th>Name</th><th>Email</th><th>Phone</th><th>Date Added</th><th>Notes</th><th></th></tr>
          </thead>
          <tbody>
            {subs.length === 0 ? (
              <tr><td colSpan={6} className="text-black/40 text-center py-8">No subscribers yet</td></tr>
            ) : subs.map((s) => (
              <tr key={s.id} data-testid={`admin-subscriber-row-${s.email}`}>
                <td className="font-display">{s.name}</td>
                <td>{s.email}</td>
                <td>{s.phone || "—"}</td>
                <td className="text-xs text-black/55">{new Date(s.date_added).toLocaleDateString()}</td>
                <td className="text-xs text-black/55">{s.notes || "—"}</td>
                <td>
                  <button onClick={() => remove(s.id)} className="p-2 hover:bg-black/5" data-testid={`admin-subscriber-delete-${s.email}`}>
                    <Trash2 size={14} />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <p className="mt-4 text-xs text-black/55">CSV columns: <code>name, email, phone, notes</code></p>
    </div>
  );
};

export default AdminSubscribers;
